import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Popover, Tooltip } from "@mui/material";
import clsx from "clsx";
import ElipsisCell from "../../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../../components/ElipsisText";
import Link from "../../../../../components/Link";
import Badge from "../../../../../components/Badge";
import Button from "../../../../../components/Button";
import Checkbox from "../../../../../components/Checkbox";
import { RowClassName, AuthRoutes, RouteParamsTypes, Modules } from "../../../../../enums";
import { TableContext } from "../../../../../contexts/TableContext";
import { VALIDATION_FIELD_MAPPING, RESERVATION_STATUS } from "../../../../../utils/appConstant";
import { formatDollarAmount } from "../../../../../utils/methods";

type ActionVariant = 'success' | 'danger' | 'warning' | 'secondary' | 'primary';

const ACTION_BADGE_VARIANT: Record<string, ActionVariant> = {
  verified: 'success',
  ignored: 'danger',
  need_to_verify: 'warning',
  partial_payment: 'primary',
};

const TEXT_CLASS_VARIANT: Record<string, string> = {
  Matched: 'text-success',
  Unmatched: 'text-danger',
  Pending: 'text-warning'
};

interface RowProps {
  item: any;
  className?: RowClassName;
  // Passed via rowProps spread from TableBox
  selectedIds: string[];
  onSelect: (id: string) => void;
  onPayoutAmtClick: (item: any) => void;
  onNoteClick: (item: any) => void;
}

const Row: React.FC<RowProps> = ({
  item,
  className,
  selectedIds,
  onSelect,
  onPayoutAmtClick,
  onNoteClick,
}) => {
  const { t } = useTranslation();
  const { headCells } = useContext(TableContext);

  // ---- Property node (with link to property view — same as reservation row) ----
  const propertyNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 34 }}>
        <img
          width={32}
          height={32}
          src={`/assets/images/${item?.platform_name}.svg`}
          alt={item?.platform_name}
        />
      </div>
      <div style={{ width: 'calc(100% - 44px)' }}>
        <ElipsisText
          width="100%"
          text={item?.property_name || '-'}
          node={
            <Link
              text={item?.property_name || '-'}
              href={`${Modules.reservations}${RouteParamsTypes.view}/${item?.reservation_id}`}
              target="_blank"
            />
          }
          className="h6 mb-1 text-decoration-underline hyperlink"
        />
        <ElipsisText width="100%" text={item?.property_city || '-'} />
      </div>
    </div>
  );

  // ---- Reservation node: guest name / status / check-in-out ----
  const reservationNode = (
    <div>
      <ElipsisText width="100%" text={item?.guest_name || '-'} className="h6 mb-1" />
      <ElipsisText
        width="100%"
        text={item?.reservation_status_name || '-'}
        className={clsx(
          (item?.reservation_status === RESERVATION_STATUS.cancelled ||
            item?.reservation_status === RESERVATION_STATUS.expired) &&
          'text-danger'
        )}
      />
      <ElipsisText
        width="100%"
        text={
          item?.check_in_date && item?.check_out_date
            ? `${item.check_in_date} - ${item.check_out_date}`
            : '-'
        }
      />
    </div>
  );

  // ---- Host Payout node (amount only, no tooltip/reviewed section) ----
  const hostPayoutNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item?.host_payout ? formatDollarAmount(item.host_payout) : '-'}</span>
    </div>
  );

  // ---- Payout Details node: date / bank account ----
  const payoutDetailsNode = (
    <div>
      <ElipsisText width="100%" text={item?.payout_date || '-'} className="h6 mb-1" />
      <ElipsisText width="100%" text={item?.bank_account || '-'} />
    </div>
  );

  // ---- Payout AMT node: blue + dotted bottom border, clickable → modal ----
  const payoutAmtNode = (
    <div
      className="fw-semibold text-primary cursor-pointer"
      style={{ borderBottom: '2px dotted', display: 'inline-block' }}
      onClick={() => onPayoutAmtClick(item)}
    >
      {item?.payout_amount ? formatDollarAmount(item.payout_amount) : '-'}
    </div>
  );

  // ---- Action node: badge + optional sticky-note icon with popover (same as transaction type) ----
  const actionNode = (
    <div className="d-flex align-items-center">
      {item?.action ? (
        <Badge
          text={item.action_name}
          variant={ACTION_BADGE_VARIANT[item.action] ?? 'secondary'}
        />
      ) : (
        <span>-</span>
      )}
      {item?.notes && (
        <Tooltip
          title={item.notes}
          arrow
          classes={{
            tooltip: 'tooltip',
          }}
        >
          <i className="ri-sticky-note-line ms-1 cursor-pointer text-primary" />
        </Tooltip>
      )}
    </div>
  );

  // ---- Actions node: Add Note button + Last Updated tooltip — same as transaction/reservation row ----
  const noteActionNode = (
    <div className="d-flex align-items-sm-center justify-content-sm-start">
      {item?.payout_id ? (
        <i className="ri-edit-box-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill" onClick={() => onNoteClick(item)}></i>
      ) : (
        <i className="ri-edit-box-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill" onClick={() => onNoteClick(item)}></i>
      )}

      <Tooltip
        title={
          <>
            <div>{t('reconciliation.payout.list.pageTexts.table.lastUpdatedBy')} {item?.updater_name || '-'}</div>
            <div>{t('reconciliation.payout.list.pageTexts.table.lastUpdated')} {item?.updated_at || '-'}</div>
          </>
        }
        arrow
        classes={{ tooltip: 'tooltip' }}
      >
        <i className="ri-timer-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"></i>
      </Tooltip>
    </div >
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.select) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="form-floating form-floating-outline">
                <Checkbox
                  variant="primary"
                  className="d-checkbox mb-0"
                  checked={selectedIds.includes(item?.id)}
                  name={VALIDATION_FIELD_MAPPING.select}
                  onChange={() => onSelect(item?.id)}
                />
              </div>
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.property) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={propertyNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.reservation) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={reservationNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.hostPayout) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={hostPayoutNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.payoutDetails) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={payoutDetailsNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.payoutAmt) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={payoutAmtNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.payoutMatchStatus) {
          return (
            <td key={headCell.id} width={headCell.width}>
              {item?.payout_match_status ? (
                <span className={TEXT_CLASS_VARIANT[item.payout_match_status]}>{item.payout_match_status}</span>
              ) : (
                <span>-</span>
              )}
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.action) {
          return (
            <td key={headCell.id} width={headCell.width}>
              {actionNode}
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.noteAction) {
          return (
            <td key={headCell.id} width={headCell.width}>
              {noteActionNode}
            </td>
          );
        }

        return <ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] ?? '-'} />;
      })}
    </tr>
  );
};

export default Row;
